import { useState, useRef, useEffect, useMemo } from "react";
import { useAppData } from "@/data/AppDataContext";
import { useRole } from "@/data/RoleContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Phone, Send, MessageSquare, User, Clock, CheckCircle,
  RotateCcw, AlertTriangle, FileSignature, Package,
} from "lucide-react";
import { toast } from "sonner";
import { format } from "date-fns";
import { formatSafe } from "@/lib/dateUtils";
import type { Message } from "@/data/communicationTypes";
import type { BuildReviewEvent } from "@/data/buildReviewTypes";
import { REVISION_REASON_LABELS } from "@/data/buildReviewTypes";
import { createJobMessage, deriveJobParticipants } from "@/data/jobMessageTypes";
import { JobMessageService } from "@/services/job-message.service";

interface JobActivityTimelineProps {
  jobId: string;
  contactName: string;
  contactPhone?: string;
  salesRep?: string;
  buildReviewEvents?: BuildReviewEvent[];
  compact?: boolean;
}

type TimelineEntry = {
  id: string;
  type: "message" | "status" | "approval" | "revision" | "change_order";
  timestamp: string;
  time: string;
  user: string;
  body: string;
  detail?: string;
  icon: React.ReactNode;
  color: string;
};

const ACTION_DISPLAY: Record<string, { label: string; icon: React.ReactNode; color: string; type: TimelineEntry["type"] }> = {
  approved: { label: "Approved — Sent to Material Planning", icon: <CheckCircle className="h-3 w-3" />, color: "text-success", type: "approval" },
  final_approved: { label: "Final Review Approved", icon: <CheckCircle className="h-3 w-3" />, color: "text-success", type: "approval" },
  revision_requested: { label: "Revision Requested", icon: <RotateCcw className="h-3 w-3" />, color: "text-destructive", type: "revision" },
  sent_to_sales: { label: "Sent to Sales for Confirmation", icon: <Send className="h-3 w-3" />, color: "text-primary", type: "status" },
  sales_confirmed: { label: "Sales Confirmed", icon: <CheckCircle className="h-3 w-3" />, color: "text-primary", type: "approval" },
  sales_resubmitted: { label: "Resubmitted for Review", icon: <RotateCcw className="h-3 w-3" />, color: "text-primary", type: "status" },
  change_order_created: { label: "Change Order Created", icon: <FileSignature className="h-3 w-3" />, color: "text-warning", type: "change_order" },
  customer_signed: { label: "Customer Signed", icon: <FileSignature className="h-3 w-3" />, color: "text-success", type: "approval" },
  submitted_for_review: { label: "Submitted for Review", icon: <Package className="h-3 w-3" />, color: "text-primary", type: "status" },
};

export function JobActivityTimeline({ jobId, contactName, contactPhone, salesRep, buildReviewEvents = [], compact }: JobActivityTimelineProps) {
  const reviewEvents = buildReviewEvents;
  const { messages, setMessages, jobMessages, setJobMessages, jobs, jobMessagesLoading, conversationsLoading } = useAppData();
  const { role } = useRole();
  const [newMessage, setNewMessage] = useState("");
  const scrollRef = useRef<HTMLDivElement>(null);
  const displaySalesRep = salesRep?.trim() || "Unassigned";

  const currentUser = role === "sales" ? displaySalesRep : "Ops Reviewer";
  const currentRole = role === "sales" ? "sales" : "reviewer";
  const job = jobs.find(j => j.id === jobId);
  const threadId = `REVIEW-${jobId}`;
  const isLoading = jobMessagesLoading || conversationsLoading;

  // Load persisted messages from DB on mount and add to thread
  useEffect(() => {
    if (!jobId) return;
    JobMessageService.getByJobNumber(jobId).then(({ data }) => {
      if (!data || data.length === 0) return;
      // Inject DB messages into the messages thread so they appear in the timeline
      setMessages(prev => {
        const existingIds = new Set(prev.map(m => m.id));
        const toAdd = data
          .filter(m => !existingIds.has(m.id))
          .map(m => ({
            id: m.id,
            conversationId: threadId,
            direction: "outbound" as const,
            channel: "note" as const,
            body: m.body,
            sender: m.senderName,
            timestamp: m.timestamp,
          }));
        return toAdd.length > 0 ? [...prev, ...toAdd] : prev;
      });
    });
  }, [jobId, threadId, setMessages]);

  const timeline = useMemo((): TimelineEntry[] => {
    const entries: TimelineEntry[] = [];

    // Messages
    messages
      .filter((m) => m.conversationId === threadId)
      .forEach((msg) => {
        entries.push({
          id: msg.id,
          type: "message",
          timestamp: msg.timestamp,
          time: formatSafe(msg.timestamp, "MMM d, h:mm a"),
          user: msg.sender,
          body: msg.body,
          icon: <MessageSquare className="h-3 w-3" />,
          color: "text-muted-foreground",
        });
      });

    // Build review events for this job
    reviewEvents
      .filter((e) => e.jobId === jobId)
      .forEach((evt) => {
        const display = ACTION_DISPLAY[evt.action] || { label: evt.action, icon: <AlertTriangle className="h-3 w-3" />, color: "text-muted-foreground", type: "status" as const };
        let detail = evt.revisionNotes || evt.notes || "";
        if (evt.revisionReason) {
          detail = `Reason: ${REVISION_REASON_LABELS[evt.revisionReason]}${detail ? ` — ${detail}` : ""}`;
        }
        if (evt.materialChanges) {
          detail += `${detail ? "\n" : ""}Material changes: ${evt.materialChanges}`;
        }
        entries.push({
          id: evt.id,
          type: display.type,
          timestamp: evt.timestamp,
          time: formatSafe(evt.timestamp, "MMM d, h:mm a"),
          user: evt.performedBy === "ops_manager" ? "Ops Reviewer" : evt.performedBy === "sales" ? displaySalesRep : evt.performedBy,
          body: display.label,
          detail: detail || undefined,
          icon: display.icon,
          color: display.color,
        });
      });

    return entries.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
  }, [messages, reviewEvents, jobId, threadId, salesRep]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [timeline.length]);

  function handleSend() {
    if (!newMessage.trim()) return;
    const now = new Date().toISOString();

    const msg: Message = {
      id: `MSG-${Date.now()}`,
      conversationId: threadId,
      direction: "outbound",
      channel: "note",
      body: newMessage.trim(),
      sender: currentUser,
      timestamp: now,
    };
    setMessages((prev) => [...prev, msg]);

    // Build recipient list from job participants
    const participants = job ? deriveJobParticipants(job) : null;
    const recipientIds: string[] = [];
    if (participants) {
      if (currentRole === "reviewer") {
        recipientIds.push(participants.salesRepId);
        if (participants.salesManagerId) recipientIds.push(participants.salesManagerId);
      } else if (currentRole === "sales") {
        if (participants.reviewerId) recipientIds.push(participants.reviewerId);
        if (participants.opsManagerId) recipientIds.push(participants.opsManagerId);
      }
    }

    const jobMsg = createJobMessage(
      jobId,
      newMessage.trim(),
      { id: currentUser, name: currentUser, role: currentRole as any },
      recipientIds,
      { context: "Build Review", priority: "normal" },
    );
    setJobMessages((prev) => [...prev, jobMsg]);

    // Persist to Supabase — fire-and-forget
    JobMessageService.create(jobMsg, jobId).catch((err) =>
      console.error("[JobActivityTimeline] Failed to persist message:", err)
    );

    setNewMessage("");
    toast.success("Message sent");
  }

  function handleCallSalesRep() {
    if (contactPhone) {
      window.open(`tel:${contactPhone}`, "_self");
      toast.info(`Calling ${displaySalesRep}...`);
    } else {
      toast.info("No phone number available");
    }
  }

  const TYPE_BG: Record<TimelineEntry["type"], string> = {
    message: "",
    status: "bg-muted/30",
    approval: "bg-success/5",
    revision: "bg-destructive/5",
    change_order: "bg-warning/5",
  };

  return (
    <section className="space-y-2.5">
      <h3 className="text-[13px] font-bold text-foreground uppercase tracking-wider flex items-center gap-2">
        <Clock className="h-4 w-4 text-muted-foreground" />
        Job Activity Timeline
      </h3>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button size="sm" variant="outline" className="h-7 text-[11px] gap-1" onClick={handleCallSalesRep}>
          <Phone className="h-3 w-3" />
          Call {displaySalesRep.split(" ")[0] || "Unassigned"}
        </Button>
      </div>

      {/* Timeline */}
      <div className="rounded-lg border border-border overflow-hidden bg-card">
        <ScrollArea className={compact ? "max-h-[180px]" : "max-h-[250px]"} ref={scrollRef}>
          <div className="p-2.5 space-y-1">
            {timeline.length === 0 ? (
              <div className="text-center py-4 text-muted-foreground">
                <Clock className="h-5 w-5 mx-auto mb-1 opacity-30" />
                <p className="text-[11px]">No activity yet</p>
                <p className="text-[10px] mt-0.5 text-muted-foreground/60">
                  Actions, messages, and status changes appear here.
                </p>
              </div>
            ) : (
               timeline.map((entry) => {
                return (
                  <div key={entry.id} className={`flex gap-2 rounded-md px-2 py-1.5 ${TYPE_BG[entry.type]}`}>
                    <div className={`h-5 w-5 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${
                      entry.type === "message" ? "bg-muted/50" :
                      entry.type === "approval" ? "bg-success/15" :
                      entry.type === "revision" ? "bg-destructive/15" :
                      entry.type === "change_order" ? "bg-warning/15" :
                      "bg-primary/15"
                    }`}>
                      <span className={entry.color}>{entry.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-foreground">{entry.user}</span>
                        <span className="text-[10px] text-muted-foreground/50">{entry.time}</span>
                        {entry.type !== "message" && (
                          <span className={`text-[9px] font-semibold uppercase tracking-wider ${entry.color}`}>
                            {entry.type.replace("_", " ")}
                          </span>
                        )}
                      </div>
                      <p className={`text-[12px] mt-0.5 ${entry.type === "message" ? "text-foreground/80" : `font-semibold ${entry.color}`}`}>
                        {entry.body}
                      </p>
                      {entry.detail && (
                        <p className="text-[11px] text-muted-foreground mt-0.5 whitespace-pre-wrap">{entry.detail}</p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Input */}
        <div className="border-t border-border p-2 flex gap-2">
          <Textarea
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Add a message..."
            className="text-[12px] min-h-[40px] max-h-[80px] bg-background flex-1 resize-none"
            rows={1}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend();
              }
            }}
          />
          <Button size="icon" className="h-8 w-8 shrink-0 self-end" onClick={handleSend} disabled={!newMessage.trim()}>
            <Send className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </section>
  );
}
